// Scripted dice physics inside a hemispherical dome + quaternion helpers.
const qMul = (a, b) => [
  a[0] * b[0] - a[1] * b[1] - a[2] * b[2] - a[3] * b[3],
  a[0] * b[1] + a[1] * b[0] + a[2] * b[3] - a[3] * b[2],
  a[0] * b[2] - a[1] * b[3] + a[2] * b[0] + a[3] * b[1],
  a[0] * b[3] + a[1] * b[2] - a[2] * b[1] + a[3] * b[0],
];
const qNorm = (q) => { const l = Math.hypot(...q) || 1; return q.map((v) => v / l); };
const qAxis = (x, y, z, a) => { const s = Math.sin(a / 2); return [Math.cos(a / 2), x * s, y * s, z * s]; };
const deg = (d) => (d * Math.PI) / 180;

export function qCss(q) {
  const w = Math.max(-1, Math.min(1, q[0]));
  const a = 2 * Math.acos(w);
  const s = Math.sqrt(1 - w * w);
  if (s < 1e-5) return 'rotate3d(1,0,0,0rad)';
  return `rotate3d(${q[1] / s},${q[2] / s},${q[3] / s},${a}rad)`;
}

// Orientation that shows `value` toward the viewer, tipped so the top face also reads.
const FACE_Q = {
  1: [1, 0, 0, 0], 2: qAxis(0, 1, 0, deg(-90)), 3: qAxis(1, 0, 0, deg(-90)),
  4: qAxis(1, 0, 0, deg(90)), 5: qAxis(0, 1, 0, deg(90)), 6: qAxis(0, 1, 0, deg(180)),
};
export const restQuat = (value, yaw = 0) => qMul(qAxis(1, 0, 0, deg(-24)), qMul(qAxis(0, 0, 1, deg(yaw)), FACE_Q[value]));

export function mulberry(seed) {
  let a = seed >>> 0;
  return () => { a += 0x6d2b79f5; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

const R = 1;
const DIE = 0.3;
const G = 12.5;
const MAX_T = 2.3;
const DT = 1 / 240;
const qConj = (q) => [q[0], -q[1], -q[2], -q[3]];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/**
 * Pre-simulates a full throw until the die naturally comes to rest, then relabels the
 * die's faces (constant body-frame rotation) so the resting pose shows `value`.
 * Motion is never corrected mid-flight, so nothing snaps or slides at the end.
 */
export function simulateThrow(value, seed) {
  const rnd = mulberry(seed);
  const r = (a, b) => a + (b - a) * rnd();
  let pos = [r(-0.1, 0.1), 0.05, r(-0.08, 0.08)];
  let v = [r(-1.6, 1.6), r(6.4, 7.2), r(-1.0, 1.0)];
  let w = [r(-18, 18), r(-18, 18), r(-18, 18)];
  let q = qNorm([r(-1, 1), r(-1, 1), r(-1, 1), r(-1, 1)]);
  const frames = [];
  let t = 0;
  let rest = 0;
  while (t < MAX_T) {
    t += DT;
    let hit = 0;
    const late = Math.max(0, (t - (MAX_T - 0.6)) / 0.6); // gently force rest near the end
    v[1] -= G * DT;
    pos = pos.map((p, i) => p + v[i] * DT);
    const onFloor = pos[1] <= 0;
    if (onFloor) {
      pos[1] = 0;
      if (v[1] < -0.6) {
        hit = Math.min(1, -v[1] / 7);
        v[1] = -v[1] * 0.42;
        const roll = cross([0, -1, 0], v).map((x) => x * 2.2); // floor contact converts slide into spin
        w = w.map((x, i) => x * 0.6 + roll[i] + r(-1, 1) * 4 * hit);
      } else {
        v[1] = 0;
      }
      const f = Math.exp(-(3.2 + late * 10) * DT);
      v[0] *= f; v[2] *= f;
      w = w.map((x) => x * Math.exp(-(4.5 + late * 14) * DT));
    } else {
      w = w.map((x) => x * Math.exp(-0.25 * DT));
    }
    const d = Math.hypot(pos[0], pos[1], pos[2]);
    const lim = R - DIE;
    if (d > lim) {
      const n = pos.map((p) => p / d);
      const vn = v[0] * n[0] + v[1] * n[1] + v[2] * n[2];
      if (vn > 0) {
        v = v.map((x, i) => x - 1.55 * vn * n[i]);
        hit = Math.max(hit, Math.min(1, vn / 6));
        const kick = cross(n, v).map((x) => x * 1.5);
        w = w.map((x, i) => x * 0.75 + kick[i]);
      }
      pos = n.map((x) => x * lim);
      if (pos[1] < 0) pos[1] = 0;
    }
    const wl = Math.hypot(...w);
    if (wl > 1e-5) q = qNorm(qMul(qAxis(w[0] / wl, w[1] / wl, w[2] / wl, wl * DT), q));
    frames.push({ t, pos: [...pos], q, hit });
    const still = onFloor && Math.hypot(v[0], v[1], v[2]) < 0.05 && wl < 0.15;
    rest = still ? rest + 1 : 0;
    if (rest > 24 && t > 1.2) break;
  }
  const k = qMul(qConj(q), restQuat(value, 0));
  const fixed = frames.map((f) => ({ ...f, q: qNorm(qMul(f.q, k)) }));
  return { frames: fixed, duration: fixed[fixed.length - 1].t };
}

export function sampleThrow(sim, time) {
  const { frames } = sim;
  const idx = Math.min(frames.length - 1, Math.max(0, Math.round(time / DT) - 1));
  return frames[idx];
}

export const DIE_RATIO = DIE;
